import { Toolbar, SearchPacks, SearchSounds, SearchCreateCuratedSets, SearchPresets, NavContext, Filters } from '@bpm-web-app/components';

import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppLink, BreakpointView, EmptyState, PrimaryPageTitle } from '@bpm-web-app/components/shared';
import { useCreateFilterParams, useViewport } from '@bpm-web-app/utils';
import { AudioFileType } from '@bpm-web-app/create-api-sdk';
import styles from './search.module.css';

type SearchQueryType = 'sounds' | 'presets' | 'packs' | 'curated-sets' | undefined;

const SEARCH_TABS: { id: SearchQueryType; label: string }[] = [
    {
        id: 'packs',
        label: 'Packs',
    },
    {
        id: 'sounds',
        label: 'Sounds',
    },
    {
        id: 'presets',
        label: 'Presets',
    },
    {
        id: 'curated-sets',
        label: 'Curated Packs',
    }
];

export default function Search() {
    const router = useRouter();
    const { isMobile } = useViewport();
    const { showSearchBar } = useContext(NavContext);
    const { type } = router.query as { type: SearchQueryType };
    const mobileTab = type || 'packs';
    const [packCount, setPackCount] = useState<number>();
    const [soundCount, setSoundCount] = useState<number>();
    const [presetCount, setPresetCount] = useState<number>();
    const [curatedSetCount, setCuratedSetCount] = useState<number>();

    const searchQuery = useCreateFilterParams();

    useEffect(() => {
        setPackCount(undefined);
        setSoundCount(undefined);
        setPresetCount(undefined);
        setCuratedSetCount(undefined);
    }, [searchQuery]);

    const { fileType } = router.query;
    const isPreset = (fileType as unknown as AudioFileType) === AudioFileType.Preset;

    const shouldShowNoResults = useMemo(() => {
        if (packCount === 0 && soundCount === 0 && presetCount === 0 && curatedSetCount === 0) return true;
        return false;
    }, [curatedSetCount, packCount, presetCount, soundCount]);

    const showNoResults = useMemo(() => {
        return (
            <EmptyState title={"Sorry, we couldn't find anything that matches your search."} subtitle="Try checking your spelling and filters, or searching for something else." verticalMargins={16} variant="white" icon="search-outline-icon" />
        );
    }, []);

    const [isLoadingPacks, setIsLoadingPacks] = useState(true);
    const [isLoadingSounds, setIsLoadingSounds] = useState(true);
    const [isLoadingPresets, setIsLoadingPresets] = useState(true);
    const [isLoadingCuratedPacks, setIsLoadingCuratedPacks] = useState(true);

    const isEverythingLoaded = useMemo(() => {
        if (
            !isLoadingPacks &&
            !isLoadingSounds &&
            !isLoadingPresets &&
            !isLoadingCuratedPacks
        ) return true;
        return false;
    }, [isLoadingPacks, isLoadingSounds, isLoadingPresets, isLoadingCuratedPacks]);

    const getDetailPages = useCallback((key?: string) => {
        switch (key) {
            case 'packs':
                return <SearchPacks onPackCount={(count) => { setPackCount(count); }} indexPage />;

            case 'sounds':
                return <SearchSounds onSoundsCount={(count) => { setSoundCount(count); }} hideShowMore />;

            case 'presets':
                return <SearchPresets onPresetsCount={(count) => { setPresetCount(count); }} hideShowMore />;

            case 'curated-sets':
                return <SearchCreateCuratedSets onCuratedSetCount={(count) => { setCuratedSetCount(count); }} indexPage />;

            default:
                return (
                    <>
                        <div className={styles['search__section-container']}>
                            <SearchPacks onPackCount={(count) => { setPackCount(count); }} setIsLoadingData={setIsLoadingPacks} isLoadingData={!isEverythingLoaded} />
                        </div>
                        {!isPreset &&
                            <div className={styles['search__section-container']}>
                                <SearchSounds onSoundsCount={(count) => { setSoundCount(count); }} setIsLoadingData={setIsLoadingSounds} isLoadingData={!isEverythingLoaded} />
                            </div>
                        }
                        <SearchPresets onPresetsCount={(count) => { setPresetCount(count); }} setIsLoadingData={setIsLoadingPresets} isLoadingData={!isEverythingLoaded} />
                        <SearchCreateCuratedSets onCuratedSetCount={(count) => { setCuratedSetCount(count); }} setIsLoadingData={setIsLoadingCuratedPacks} isLoadingData={!isEverythingLoaded} />
                    </>
                );
        }
    }, [isEverythingLoaded, isPreset]);

    const getWhichCount = useCallback((key?: string) => {
        switch (key) {
            case 'packs':
                return packCount;
            case 'sounds':
                return soundCount;
            case 'presets':
                return presetCount;
            case 'curated-sets':
                return curatedSetCount;
            default:
                return null;
        }
    }, [curatedSetCount, packCount, presetCount, soundCount]);

    const counterData = useCallback((key: string) => {
        const count = getWhichCount(key);
        if (count !== undefined) {
            return <span className={classNames(styles['search__counter'], { [styles['search__mobile-tab--active']]: mobileTab === key })}>&nbsp;{`(${count})`}</span>;
        }
        return null;
    }, [getWhichCount, mobileTab]);

    useEffect(() => {
        showSearchBar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const actualMobileTabs = useMemo(() => {
        if (isPreset) {
            return SEARCH_TABS.slice().filter(({ id }) => id !== 'sounds');
        }
        return SEARCH_TABS;
    }, [isPreset]);

    return (
        // eslint-disable-next-line @typescript-eslint/dot-notation
        <div className="spacing__window--top">
            <div className={classNames(styles['search__top-section'], 'spacing__window--horizontal',)}>
                <PrimaryPageTitle title="Search Results" noPadding />
                {!isMobile ? <Filters platform="create" showOnMobile /> : null}
            </div>
            {shouldShowNoResults ? (
                showNoResults
            ) : (<BreakpointView
                mobileChildren={
                    <>
                        <Toolbar scrollable={isMobile}>
                            <div className={classNames(styles['search__mobile-tabs-container'], 'spacing--left-first-child')}>
                                {actualMobileTabs.map(({ id, label }) => (
                                    <AppLink
                                        key={id}
                                        href={{
                                            pathname: router.pathname,
                                            query: {
                                                ...router.query,
                                                type: id,
                                            },
                                        }}
                                    >
                                        <a className={classNames(styles['search__mobile-tab'], { [styles['search__mobile-tab--active']]: mobileTab === id })}>{label}{counterData(id)}</a>
                                    </AppLink>
                                ))}
                            </div>
                        </Toolbar>
                        {getDetailPages(type || 'packs')}
                    </>
                }
                desktopChildren={getDetailPages(type)}
            />)}
        </div>
    );
}
